import React from 'react';

export type AppNotificationType = 'info' | 'success' | 'warning' | 'error';

/**
 * A persistent notification shown in the header bell (unlike toasts, notifications stay until
 * dismissed). Replaces the legacy ui-kit `NotificationProvider`/`IMessageBarMessage` store.
 */
export interface AppNotification {
  id: string;
  type: AppNotificationType;
  title?: string;
  message: React.ReactNode;
  /** Set by `readNotification` — e.g. after the user clicked through to the notification target. */
  read?: boolean;
  /** Arbitrary payload for click handlers (e.g. redirect targets). */
  data?: unknown;
  /** Invoked when the rendered notification row is clicked. */
  onClick?: (notification: AppNotification) => void;
  /** Opaque passthrough used by the legacy adapter to round-trip Fluent MessageBar props.
   *  TODO: remove once the legacy UI will be decommissioned and not used in new UI
   */
  meta?: unknown;
}

export type AddNotificationInput = Omit<AppNotification, 'id' | 'read'> & { id?: string };

interface NotificationsContextValue {
  notifications: AppNotification[];
  /** Adds a notification and returns its id. */
  addNotification: (input: AddNotificationInput) => string;
  /** Marks a notification as read (it stays listed until removed). */
  readNotification: (id: string | undefined) => void;
  removeNotification: (id: string | undefined) => void;
  removeAllNotifications: () => void;
}

let notificationIdCounter = 0;
const generateId = () => `notification-${++notificationIdCounter}-${Date.now()}`;

const NotificationsContext = React.createContext<NotificationsContextValue | undefined>(undefined);

export const NotificationsProvider = ({ children }: { children: React.ReactNode }) => {
  const [notifications, setNotifications] = React.useState<AppNotification[]>([]);

  // Stable identities (no deps): consumers such as the SSE listener in the featurestore
  // `useHandleNotifications` capture these once and must not re-subscribe on every notification.
  const addNotification = React.useCallback((input: AddNotificationInput): string => {
    const id = input.id ?? generateId();
    setNotifications((prev) => [...prev, { ...input, id }]);
    return id;
  }, []);

  const readNotification = React.useCallback((id: string | undefined) => {
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
  }, []);

  const removeNotification = React.useCallback((id: string | undefined) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const removeAllNotifications = React.useCallback(() => {
    setNotifications([]);
  }, []);

  const value = React.useMemo(
    () => ({ notifications, addNotification, readNotification, removeNotification, removeAllNotifications }),
    [notifications, addNotification, readNotification, removeNotification, removeAllNotifications]
  );

  return React.createElement(NotificationsContext.Provider, { value }, children);
};

export const useNotifications = () => {
  const context = React.useContext(NotificationsContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationsProvider');
  }
  return context;
};

/**
 * Non-throwing accessor returning `undefined` when no `NotificationsProvider` is mounted. Intended
 * for the legacy `useNotification` adapter, which historically used a default (no-op) context and
 * must not throw when rendered outside a provider (e.g. isolated unit tests / Storybook). Prefer
 * `useNotifications` in new code so missing-provider mistakes surface loudly.
 * TODO: remove once the legacy UI will be decommissioned.
 */
export const useOptionalNotifications = (): NotificationsContextValue | undefined =>
  React.useContext(NotificationsContext);
